<template>
  <div style="position: relative; min-height: 200px">

    <b-loading
      :active="items === null"
      :is-full-page="false"
    />

    <div class="actions">
      <div class="filtering">
        <div class="search">
          <b-field>
            <b-input
              v-model="query"
              placeholder="Rechercher..."
              icon="search"
              size="is-small"
              expanded
              :disabled="!items || items.length === 0"
            />
            <button
              v-if="query"
              class="button is-small"
              @click="query = ''"
            >
              <b-icon
                icon="times-circle"
                size="is-small"
              />
              <span>Annuler</span>
            </button>
          </b-field>
        </div>
        <div class="unread">
              <input type="checkbox" id="unread-checkbox" v-model="showUnread">
              <span id="unread-label">Afficher les documents non-lus</span>
          </div>

      </div>

      <div class="expand-all">
        <button
          class="button is-small"
          @click.prevent="toggleExpanded"
        >
          <b-icon
            icon="sitemap"
            size="is-small"
          />
          <span>Tout {{ expanded ? 'fermer' : 'ouvrir' }}</span>
        </button>
      </div>
    </div>

    <div v-if="query">
      <h1 class="is-size-4">Résultats de la recherche</h1>

      <b-message v-if="filteredItems.length === 0">
        Aucun résultat trouvé.
      </b-message>
    </div>

    <div v-if="!query && showUnread">
       <b-message v-if="filteredItems.length === 0">
        Aucun document non lu trouvé.
      </b-message>
    </div>

    <b-message v-if="items && items.length === 0">
      Arborescence non disponible.
    </b-message>

    <PortefeuilleArbo
      v-if="filteredItems.length > 0"
      ref="arbo"
      :items="filteredItems"
      :query="query"
      :showUnreadOnly="showUnread"
    />

  </div>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'

import PortefeuilleArbo from '@/components/Portefeuille/Arbo'

import MixinStringHelper from '@/mixins/StringHelper'
import MixinDocumentHelper from '@/mixins/DocumentHelper'

export default {
  mixins: [MixinStringHelper, MixinDocumentHelper],
  components: {
    PortefeuilleArbo,
  },
  data () {
    return {
      query: this.$route.query.search,
      items: null,
      expanded: false,
      showUnread: false
    }
  },
  computed: {
    ...mapState(['documentsRead']),
    filteredItems () {
      if (!this.items || this.items.length === 0) return []

      let tempItems = [...this.items]
      if (this.query || this.isShowUnread()) {
        let reg
        if (!this.query) {
          reg = new RegExp(/.*/, 'i')
        } else {
          reg = new RegExp('' + this.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i')
        }

        tempItems = tempItems.map(item => {
          // Normalize item name before trying to match for reg exp
          if (this.normalizeString(item.name).match(reg)) { return item }

          // Check if level item contains
          // - documents
          // - document collections
          // - documents inside document collections
          // that match the query
          const hasDocumentsMatching = item.documents.find(i => this.normalizeString(i.name).match(reg))
          const hasCollectionsMatching = item.collections.find(i => this.normalizeString(i.name).match(reg))
          const hasDocumentsInCollectionsMatching = item.collections.some(col => col.documents.find(i => this.normalizeString(i.name).match(reg)))
          if (!hasDocumentsMatching && !hasCollectionsMatching && !hasDocumentsInCollectionsMatching) return null

          const itemClone = JSON.parse(JSON.stringify(item))
          itemClone.documents = JSON.parse(JSON.stringify(item.documents))
            .filter(i => this.normalizeString(i.name).match(reg))

          if (hasDocumentsInCollectionsMatching) {
            let tempCollections = []

            item.collections.forEach(col => {
              let colClone = JSON.parse(JSON.stringify(col))
              let matchingDocuments = col.documents.filter(i => this.normalizeString(i.name).match(reg))
              if (matchingDocuments && matchingDocuments.length > 0) {
                colClone.documents = matchingDocuments
                tempCollections.push(colClone)
              }
            })
            itemClone.collections = tempCollections
          } else if (hasCollectionsMatching) {
            itemClone.collections = JSON.parse(JSON.stringify(item.collections))
              .filter(i => this.normalizeString(i.name).match(reg))
          }
          return itemClone
        })
          .filter(i => !!i) // remove null values

        if (this.isShowUnread()) {
          let levelsWithUnreadDocs = []
          // Find levels with unread docs
          tempItems.forEach(item => {
            // If item has documents, it is considered a level-type object
            if (item.documents.length > 0) {
              // Is there even one unread document?
              if (item.documents.some(doc => !this.isRead(doc.id))) {
                // Add to levelsWithUnreadDocs array if it isn't there already
                if (!levelsWithUnreadDocs.some(elem => elem['id'] === item.id)) {
                  levelsWithUnreadDocs.push(JSON.parse(JSON.stringify(item)))
                }
              }
            }

            // If item has collections, it is considered a level-type object
            if (item.collections.length > 0) {
              // Check each collection if there's unread documents inside
              item.collections.forEach(col => {
                if (col.documents.some(doc => !this.isRead(doc.id))) {
                  // Add to levelsWithUnreadDocs array if it isn't there already
                  if (!levelsWithUnreadDocs.some(elem => elem['id'] === item.id)) {
                    levelsWithUnreadDocs.push(JSON.parse(JSON.stringify(item)))
                  }
                }
              })
            }
          })

          // When regExp is set to include all files, let's include level items as well
          if (reg.source === '.*') {
            // Find every parent level for levels with unread docs
            levelsWithUnreadDocs.forEach((level) => {
              let parent = level.parent
              while (parent !== 0) {
                let parentItem = tempItems.find(item => item.id === parent)
                if (parentItem) {
                  if (!levelsWithUnreadDocs.some(elem => elem['id'] === parentItem.id)) {
                    levelsWithUnreadDocs.push(parentItem)
                  }
                  parent = parentItem.parent
                }
              }
            })
          }

          // Remove read documents from levels
          levelsWithUnreadDocs.forEach((level) => {
            let newDocs = []
            // Place all unread documents in this level to newDocs
            level.documents.forEach(doc => !this.isRead(doc.id) ? newDocs.push(doc) : _)
            level.documents = newDocs
            let newCols = []
            level.collections.forEach(docCol => {
              // Create document collection from existing collection to hold unread documents
              let tempCol = JSON.parse(JSON.stringify(docCol))
              // Clear documents
              tempCol.documents = []
              docCol.documents.forEach(doc => {
                if (!this.isRead(doc.id)) {
                  // Add document to temporary collection
                  tempCol.documents.push(doc)
                }
              })

              if (docCol.documents.length > 0) {
                newCols.push(tempCol)
              }
            })
            level.collections = newCols
          })

          tempItems = levelsWithUnreadDocs.filter(i => !!i)
        }
      }

      // Sort levels
      tempItems.sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true }))
      tempItems.forEach(function (item) {
        // Sort level's documents
        if (item.documents) {
          item.documents.sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true }))
        }
        // Sort level's collections
        if (item.collections) {
          item.collections.sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true }))

          // Sort collection's documents
          item.collections.forEach(col => col.documents.sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true })))
        }
      })
      return tempItems
    }
  },
  watch: {
    '$route.params.bien' () {
      this.getData()
      this.query = ''
    },
    'documentsRead.length' () {
      this.setItemsData()
    },
    expanded () {
      if (this.$refs.arbo) {
        this.$refs.arbo.toggleAll(this.expanded)
      }
    },
    query () {
      if ((this.showUnread || this.query) && !this.expanded) {
        this.expanded = true
      } else if (!this.showUnread && !this.query && this.expanded) {
        this.expanded = false
      }
    },
    showUnread () {
      this.toggleShowUnread()
      if ((this.showUnread || this.query) && !this.expanded) {
        this.expanded = true
      } else if (!this.showUnread && !this.query && this.expanded) {
        this.expanded = false
      }
    }
  },
  created () {
    this.getData()
  },
  methods: {
    async getData () {
      this.expanded = false
      this.showUnread = false
      this.items = null
      try {
        this.$store.commit('inv/ARBO_NOT_RESET')
        await this.$store.dispatch('inv/getArboNot', this.$route.params.bien)
      } catch (e) {
        this.apiError(e)
      }
      this.setItemsData()
    },
    setItemsData () {
      let items = this.$store.state.inv.arboNot
      items.forEach((item, i) => this.setItemData(item, items))
      this.items = items
    },
    setItemData (item, items) {
      // Sanity: recursive
      if (item.documentsTotal === 'number') {
        return
      }

      // unread documents
      var documentIds = item.documents.map(i => i.id)
      item.documentsReadCount = this.$store.state.documentsRead.filter(i => documentIds.indexOf(i) !== -1).length
      item.documentsTotal = item.documents.length

      // Add collection docs
      item.collections.forEach(col => {
        item.documentsTotal += col.documents.length

        // unread documents
        documentIds = col.documents.map(i => i.id)
        item.documentsReadCount += this.$store.state.documentsRead.filter(i => documentIds.indexOf(i) !== -1).length
      })

      // subfolders?
      const subfolders = items.filter(j => j.parent === item.id && j.level === item.level + 1)
      item.hasSubfolders = !!subfolders

      // aggregate subfolder's documents count
      subfolders.forEach(subfolder => {
        // Recursive call
        if (typeof subfolder.documentsTotal !== 'number') { this.setItemData(subfolder, items) }

        item.documentsReadCount += subfolder.documentsReadCount
        item.documentsTotal += subfolder.documentsTotal
        item.modifiedCount += subfolder.modifiedCount
      })
    },
    toggleExpanded () {
      if (this.$refs.arbo) {
        this.expanded = !this.expanded
      }
    },
    isShowUnread () {
      return this.$store.state.inv.showUnreadOnly
    },
    toggleShowUnread () {
      this.$store.dispatch('inv/setShowUnreadOnly', this.showUnread)
    }
  },
}
</script>

<style lang="scss" scoped>
h1 {
  margin: 1.5em 0 .5em;
}
</style>
